import { useRef } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { OrderBy, ReviewedStatus } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/booking_submission.pb';
import { ScheduleQueries } from '@frontend/api-schedule';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { InfiniteQueryResult } from '@frontend/types';
import { convertScheduleRequestV3ToV2 } from './useGetScheduleRequest';

const defaultPageConfig = {
  limit: 25,
  offset: 0,
};

type QueryResponse = InfiniteQueryResult<Record<'rows', ScheduleRequest[]>> & {
  refetchQueries: () => void;
};

export const useCombinedScheduleRequest = (locationId: string, combineBookingRequests = false): QueryResponse => {
  // Track which query version is currently being used
  const fetchV2DataRef = useRef(false);

  // TODO: Replace this with useGetScheduleRequestInfiniteListQuery hook
  const scheduleRequestListQueryV2 = ScheduleQueries.useGetScheduleRequestListInfiniteQuery(
    {
      locationId,
      limit: defaultPageConfig.limit,
      offset: defaultPageConfig.offset,
      statuses: 'Pending',
      orderBy: '-createdAt',
    },
    { enabled: !!locationId && combineBookingRequests }
  );

  const scheduleRequestListQueryV3 = SchedulerV3.Queries.useListBookingSubmissionsInfiniteQuery({
    request: {
      locationsIds: locationId ? [locationId] : [],
      limit: defaultPageConfig.limit,
      reviewedStatuses: [ReviewedStatus.PENDING],
      orderBy: OrderBy.CREATED_AT, // By default it will be order by descending
    },
    options: {
      enabled: !!locationId && combineBookingRequests,
      select: (data) => ({
        ...data,
        pages: data.pages.map((page, index) => ({
          rows: (page.bookingSubmissions ?? []).map<ScheduleRequest>(convertScheduleRequestV3ToV2),
          totalCount: page.totalCount ?? 0,
          page: index + 1,
          totalPages: Math.ceil((page.totalCount ?? 0) / 25),
        })),
      }),
    },
  });

  const refetchQueries = () => {
    scheduleRequestListQueryV2.refetch();
    scheduleRequestListQueryV3.refetch();
  };

  // Combine the data from both queries
  let queryResult;
  let fetchNextPage;
  let combinedPages = [];

  if (!fetchV2DataRef.current) {
    const totalRequestsCount = Number(scheduleRequestListQueryV3.data?.pages?.[0]?.totalCount) || 0;

    // Check if there is no data for V3
    if (!scheduleRequestListQueryV3.isLoading && totalRequestsCount === 0) {
      fetchV2DataRef.current = true;
    }

    const fetchedRequestsCount = scheduleRequestListQueryV3.data?.pages.reduce(
      (acc, page) => acc + (page?.rows?.length || 0),
      0
    );

    const hasAllDataFetched = !scheduleRequestListQueryV3.isLoading && fetchedRequestsCount === totalRequestsCount;

    // Check if all the data has been fetched from V3
    if (hasAllDataFetched) {
      fetchV2DataRef.current = true;
    }

    fetchNextPage = async () => {
      const fetchNextResult = await scheduleRequestListQueryV3.fetchNextPage();
      if (!fetchNextResult.hasNextPage) {
        fetchV2DataRef.current = true;
      }
    };

    combinedPages = [
      ...(scheduleRequestListQueryV3.data?.pages || []),
      ...((hasAllDataFetched &&
        scheduleRequestListQueryV2.data?.pages?.map((page) => ({
          ...page,
          rows: page.rows.map((row) => ({ ...row, linkHash: 'v2' })),
        }))) ||
        []),
    ];

    queryResult = scheduleRequestListQueryV3;
  } else {
    /*
      `linkHash` is used to differentiate between V2 and V3 requests. Using this field to mark V2 requests without changing the response type,
      as this field is part of Request schema and does not have any other use case. Merging v2 and v3 will be removed once all users have migrated to V3.
     */
    combinedPages = [
      ...(scheduleRequestListQueryV3.data?.pages || []),
      ...(scheduleRequestListQueryV2.data?.pages?.map((page) => ({
        ...page,
        rows: page.rows.map((row) => ({ ...row, linkHash: 'v2' })),
      })) || []),
    ];

    queryResult = scheduleRequestListQueryV2;
    fetchNextPage = scheduleRequestListQueryV2.fetchNextPage;
  }

  return {
    ...queryResult,
    data: {
      pages: combinedPages,
      pageParams: queryResult.data?.pageParams || [],
    },
    fetchNextPage: fetchNextPage ?? queryResult.fetchNextPage,
    refetchQueries,
  } as QueryResponse;
};
